// src/views/ConditionDetailPage.js  (or .jsx)
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { conditionsData } from "./conditionsData"; // both files in views/
import "../styles/ConditionDetailPage.css";
import "../styles/Home.css";

const ConditionDetailPage = () => {
  const navigate = useNavigate();

  // MUST match your route: <Route path="/conditions/:id" ... />
  const { id } = useParams();

  // Debug logs: open DevTools -> Console to see these

  // Normalize param (handles spaces / case / URL encoding)
  const normalizedId = decodeURIComponent(id || "").toLowerCase();

  // Try to find by id or name in a forgiving way
  const condition =
    conditionsData.find(
      (c) =>
        (c.id && c.id.toLowerCase() === normalizedId) ||
        (c.name && c.name.toLowerCase() === normalizedId)
    ) || null;

  if (!condition) {
    // This is the screen you're currently seeing
    return (
      <div className="condition-detail-page">
        <div className="condition-detail-inner">
          <button
            className="cdp-back-link"
            type="button"
            onClick={() => navigate(-1)}
          >
            <span className="cdp-back-icon">←</span>
            Back to Conditions
          </button>
          <div className="cdp-main-card">
            <h2 className="cdp-title">Condition not found</h2>
            <p className="cdp-description">
              We couldn’t find details for this condition. It may have been
              renamed or removed. Browse the full list to find what you need.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Map your actual fields from conditionsData.js
  const {
    name,
    category,
    stat,
    longDescription,
    shortDescription,
    commonSymptoms,
    treatmentOptions,
  } = condition;

  const mainDescription = longDescription || shortDescription || "";
  const badge = category;
  const symptoms = commonSymptoms || [];
  const treatments = treatmentOptions || [];

  return (
    <div className="condition-detail-page">
      <header className="home-nav conditions-nav">
            <Link to="/" className="home-nav-left" aria-label="Youro home">
              <div className="home-nav-logo-circle">Y</div>
              <span className="home-nav-logo-text">Youro</span>
            </Link>
            <nav className="home-nav-center">
              <button
                className="home-nav-link"
                onClick={() => navigate("/about")}
              >
                About
              </button>
              <button
                className="home-nav-link home-nav-link-active"
              >
                Find Your Diagnosis
              </button>
              <button
                className="home-nav-link"
                onClick={() => navigate("/assessment")}
              >
                Symptom Assessment
              </button>
            </nav>
            <div className="home-nav-right">
              <button
                className="home-nav-signin"
                onClick={() => navigate("/login")}
              >
                Sign In
              </button>
              <button
                className="home-nav-get-started"
                onClick={() => navigate("/signup")}
              >
                Get Started
              </button>
            </div>
      </header>
      <div className="condition-detail-inner">
        {/* Back link */}
        <button
          className="cdp-back-link"
          type="button"
          onClick={() => navigate("/conditions")}
        >
          <span className="cdp-back-icon">←</span>
          Back to Conditions
        </button>

        {/* Top info card */}
        <section className="cdp-main-card">
          <h1 className="cdp-title">{name}</h1>

          {badge && <span className="cdp-badge">{badge}</span>}

          {stat && <p className="cdp-stat">{stat}</p>}

          {mainDescription && (
            <p className="cdp-description">{mainDescription}</p>
          )}
        </section>

        {/* Symptoms / Treatments side-by-side */}
        <section className="cdp-two-column-grid">
          <article className="cdp-info-card">
            <h2 className="cdp-info-title">Common Symptoms</h2>
            <p className="cdp-info-subtitle">
              Signs that may indicate this condition
            </p>

            <ul className="cdp-bullet-list">
              {symptoms.map((item, idx) => (
                <li className="cdp-bullet-item" key={idx}>
                  <span className="cdp-bullet-dot" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          <article className="cdp-info-card">
            <h2 className="cdp-info-title">Treatment Options</h2>
            <p className="cdp-info-subtitle">
              Evidence-based approaches we offer
            </p>

            <ul className="cdp-bullet-list">
              {treatments.map((item, idx) => (
                <li className="cdp-bullet-item" key={idx}>
                  <span className="cdp-bullet-dot" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>
        </section>

        {/* CTA card at the bottom */}
        <section className="cdp-cta-card">
          <h2 className="cdp-cta-title">Ready to get started?</h2>
          <p className="cdp-cta-subtitle">
            Take the first step toward better health
          </p>

          <div className="cdp-cta-buttons">
            <button
              type="button"
              className="cdp-btn-primary"
              onClick={() => navigate(`/assessment/${id}`)}
            >
              Take Questionnaire <span className="cdp-btn-arrow">→</span>
            </button>

            <button
              type="button"
              className="cdp-btn-secondary"
              onClick={() => navigate("/login")}
            >
              Schedule Consultation
            </button>
          </div>

          <div className="cdp-expect-wrapper">
            <p className="cdp-expect-title">What to expect:</p>
            <ul className="cdp-expect-list">
              <li>Comprehensive assessment</li>
              <li>Personalized treatment plan</li>
              <li>Ongoing support and monitoring</li>
              <li>Access to our care team</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ConditionDetailPage;
